import { useMutation } from "@apollo/client/react";
import { alert } from "@components/common/Alert";
import PremiumFeatureGate from "@components/common/premium/PremiumFeatureGate";
import { DSButton } from "@components/design-system/molecules";
import { faCheck } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import UPDATE_USER_FF_SEGMENT from "@graphql/mutations/UpdateUserFfSegment";
import { usePremiumFeature } from "@hooks";
import Twemoji from "@src/Twemoji";
import { toTitleCase } from "@src/utils";
import { trackOperation } from "@src/utils/operationTracking";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useState } from "react";

import CompanionModal from "./CompanionModal";

export const CompanionConsentModal = ({ onDismiss }) => {
  const { companion } = useFlags();
  const [isChecked, setIsChecked] = useState(companion);
  const { hasPaidSubscription } = usePremiumFeature();
  const [updateUserFfSegment, { loading }] = useMutation(
    UPDATE_USER_FF_SEGMENT,
  );

  useEffect(() => {
    setIsChecked(companion);
  }, [companion]);

  const onClose = () => {
    onDismiss?.();
  };

  const hasChanged = isChecked !== companion;

  const onSubmit = async () => {
    if (!hasChanged || loading) return;
    await trackOperation(
      "companion-beta-opt-in",
      async (ctx) => {
        const action = isChecked ? "ADD" : "REMOVE";
        const response = await updateUserFfSegment({
          variables: {
            action,
            segmentKey: "companion-beta-opt-in",
          },
        });

        if (!response?.data?.updateUserFfSegment?.success) {
          throw new Error();
        }

        ctx.addProperties({
          settingName: toTitleCase("companion-beta-opt-in"),
          settingKey: "companion-beta-opt-in",
          settingValue: action,
        });

        alert(
          "success",
          "You've been opted in to Companion. Consider yourself a Companion Trainer! 🦔🐘",
        );
      },
      () => {
        alert(
          "error",
          "Failed to opt you in to Companion. Please refresh the page and try again.",
        );
      },
    );
  };

  return (
    <CompanionModal title="Opt-In to Companion" onClose={onClose}>
      <p className="text-left dark:text-gray-700">
        Companion is Scribenote’s new set of AI-based tools that are
        ready to help throughout your day.
        <br />
        <br />
        Summi <Twemoji emoji="🐘" />, Quill <Twemoji emoji="🦔" />,
        and the rest of the Companions can help you summarize patient
        records, edit notes in a flash, and much more!
        <br />
        <br />
        This Beta is currently available for Scribenote Pro customers
        at no additional cost – but we need your help making our
        Companions smarter!
        <br />
        <br />
        If you’d like to help, please opt-in below.{" "}
        <strong>
          By opting in, your anonymized Companion chat data may be
          used to help improve future versions.
        </strong>
      </p>
      <p className="text-left dark:text-gray-700">
        <a
          className="text-indigo-500 underline hover:text-indigo-600"
          href="https://www.scribenote.com/companion"
          target="_blank"
          rel="noopener noreferrer"
        >
          Learn more
        </a>{" "}
        or read our{" "}
        <a
          className="text-indigo-500 underline hover:text-indigo-600"
          href="https://www.scribenote.com/terms-of-service"
          target="_blank"
          rel="noopener noreferrer"
        >
          Terms of Service
        </a>
        .
      </p>

      <label className="flex items-center my-4">
        <PremiumFeatureGate
          featureName="Companion"
          iconPosition="center"
          iconSize="sm"
        >
          <div className="flex flex-row items-center justify-center relative">
            <input
              data-testid="companion-consent-checkbox"
              checked={isChecked}
              onChange={(e) => setIsChecked(e.target.checked)}
              type="checkbox"
              className="appearance-none rounded-md bg-white border-2 border-indigo-500 checked:bg-indigo-500 dark:checked:bg-indigo-500 checked:border-transparent h-6 w-6 cursor-pointer transition-all"
            />

            {isChecked && (
              <FontAwesomeIcon
                icon={faCheck}
                className="text-white absolute pointer-events-none"
              />
            )}
          </div>
        </PremiumFeatureGate>

        <div className="text-sm leading-none ml-2 dark:text-gray-700">
          Yes, I’d like to try Companion!{" "}
        </div>
      </label>
      <div className="flex justify-center">
        <DSButton
          onClick={onSubmit}
          disabled={!hasChanged || loading || !hasPaidSubscription}
          dataTestId="companion-consent-submit-button"
        >
          Submit
        </DSButton>
      </div>
    </CompanionModal>
  );
};

export default CompanionConsentModal;
